import type { ReactNode } from 'react'
import type { SalonRow } from '@manaro/contracts'
import { cn } from '@/shared/lib/cn.ts'
import { formatDate, formatRelative } from '@/shared/lib/format.ts'
import { Badge } from '@/shared/ui/badge.tsx'
import { Button } from '@/shared/ui/button.tsx'
import {
  LOGIN_LABELS,
  SIGNUP_TRACKING_SINCE,
  bookingPageUrl,
  channelLabel,
  clientLabel,
  estimatedClient,
  planLabel,
  signupDetails,
  statusLabel,
} from '../labels.ts'
import { messengersForPhone, phoneLink, salonContactLinks } from '../contacts.ts'
import { MODERATION_LABELS, MODERATION_TONES } from '../moderation.ts'
import { ChatChips, ContactLine } from './contact-links.tsx'

// Pieces of a salon row shared by the desktop table and the phone cards.

type Owner = SalonRow['user']
type Salon = NonNullable<SalonRow['salon']>

export function Sub({ children }: { children: ReactNode }) {
  return <div className="mt-0.5 text-[12px] text-ink-muted">{children}</div>
}

export function Count({ value }: { value: number | undefined }) {
  if (value === undefined) return <span className="text-ink-faint">—</span>
  return <span className={cn('tabular', value === 0 && 'text-warn-ink')}>{value}</span>
}

// With moderation the catalog column tells the status; an older backend still gets it here.
const salonPlace = (salon: Salon): string =>
  [salon.city, salon.moderation ? null : statusLabel(salon.status)].filter(Boolean).join(' · ')

/** Under the salon name: its ID (one click selects it for a message to the developers), then city and status. */
export function SalonMeta({ salon }: { salon: Salon }) {
  const place = salonPlace(salon)

  return (
    <Sub>
      ID <span className="tabular select-all">{salon.id}</span>
      {place && ` · ${place}`}
    </Sub>
  )
}

interface ModerationCellProps {
  salon: SalonRow['salon']
  onOpen: () => void
  /** On a phone card the salons that need a decision get a full-size button. */
  layout?: 'table' | 'card'
}

/** Catalog status and the way into the review dialog. */
export function ModerationCell({ salon, onOpen, layout = 'table' }: ModerationCellProps) {
  const moderation = salon?.moderation
  if (!moderation) return <span className="text-ink-faint">—</span>

  const needsReview = moderation.state === 'pending'
  const resubmitted = needsReview && moderation.lastEvent?.action === 'resubmitted'
  const label = needsReview ? 'Проверить' : 'Открыть'

  return (
    <div className="flex flex-col items-start gap-1">
      <Badge tone={MODERATION_TONES[moderation.state]} className="whitespace-nowrap">
        {MODERATION_LABELS[moderation.state]}
      </Badge>
      {resubmitted && <span className="text-[12px] text-ink-muted">после исправлений</span>}
      {moderation.comment && (
        <span className="line-clamp-2 max-w-[200px] text-[12px] text-ink-muted max-md:max-w-none" title={moderation.comment}>
          {moderation.comment}
        </span>
      )}
      {layout === 'card' && moderation.state !== 'published' ? (
        <Button variant={needsReview ? 'primary' : 'secondary'} size="sm" className="mt-1.5 min-h-11 w-full" onClick={onOpen}>
          {needsReview ? 'Проверить салон' : 'Открыть'}
        </Button>
      ) : (
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            'min-h-11 text-[13px] font-medium hover:underline md:min-h-8',
            needsReview ? 'text-brand hover:text-brand-dark' : 'text-ink-muted hover:text-ink',
          )}
        >
          {label}
        </button>
      )}
    </div>
  )
}

export function SourceCell({ user }: { user: Owner }) {
  if (!user.signup) {
    return (
      <div title={`Регистрация до ${SIGNUP_TRACKING_SINCE}: источник тогда не сохранялся, это оценка по способу входа и имени`}>
        <div className="text-ink-muted">≈ {estimatedClient(user)}</div>
        <div className="mt-0.5 text-[12px] text-ink-faint">канал неизвестен</div>
      </div>
    )
  }

  const { client, channel } = user.signup
  const details = [clientLabel(client), signupDetails(user.signup)].filter(Boolean).join(' · ')

  if (channel === 'unknown') {
    return (
      <>
        <div className="font-medium">{clientLabel(client)}</div>
        <Sub>канал не отслеживается</Sub>
      </>
    )
  }

  return (
    <>
      <div className="font-medium">{channelLabel(channel)}</div>
      <div className="mt-0.5 text-[12px] text-ink-muted [overflow-wrap:anywhere]">
        {details}
      </div>
    </>
  )
}

/**
 * `className` sizes the email line: the table caps its width, a card lets it run full width.
 * `chats` adds Viber/WhatsApp/Telegram on the owner's number — the table has no other place for them.
 */
export function OwnerCell({ user, className, chats = false }: { user: Owner; className?: string; chats?: boolean }) {
  const name = user.name && user.name !== user.email.split('@')[0] ? user.name : null
  const details = [LOGIN_LABELS[user.login], name].filter(Boolean).join(' · ')
  const phone = user.phone ? phoneLink(user.phone) : null

  return (
    <div>
      <div className={cn('flex max-w-[220px] items-center gap-1.5', className)}>
        <a href={`mailto:${user.email}`} className="truncate font-medium hover:text-brand hover:underline" title={`Написать: ${user.email}`}>
          {user.email}
        </a>
        {user.looksLikeTest && <Badge>тест</Badge>}
      </div>
      <Sub>
        {details}
        {phone ? (
          <>
            {' · '}
            <a href={phone.href} className="tabular whitespace-nowrap hover:text-brand hover:underline" title="Позвонить владельцу">
              {phone.label}
            </a>
          </>
        ) : (
          user.phone && ` · ${user.phone}`
        )}
        {!user.crmAccess && (
          <Badge tone="warn" className="ml-1.5">
            нет входа в CRM
          </Badge>
        )}
      </Sub>
      {chats && user.phone && <ChatChips links={messengersForPhone(user.phone)} />}
    </div>
  )
}

export function SalonCell({ salon }: { salon: SalonRow['salon'] }) {
  if (!salon) return <span className="text-ink-faint">—</span>

  return (
    <>
      <a
        href={bookingPageUrl(salon)}
        target="_blank"
        rel="noreferrer"
        className="font-medium hover:text-brand hover:underline"
      >
        {salon.title}
      </a>
      <SalonMeta salon={salon} />
      <ContactLine links={salonContactLinks(salon)} />
    </>
  )
}

export function OrdersCell({ salon }: { salon: SalonRow['salon'] }) {
  if (!salon) return <span className="text-ink-faint">—</span>

  return (
    <>
      <Count value={salon.orders} />
      {salon.orders > 0 && (
        <>
          <Sub>
            за 30 дн. <span className="tabular">{salon.orders30d}</span>
          </Sub>
          {salon.lastOrderAt && <Sub>последняя {formatRelative(salon.lastOrderAt)}</Sub>}
        </>
      )}
    </>
  )
}

interface PlanCellProps {
  salon: SalonRow['salon']
  /** Set for those who may give a plan away: adds «Подарить…». */
  onGrant?: () => void
}

export function PlanCell({ salon, onGrant }: PlanCellProps) {
  if (!salon) return <span className="text-ink-faint">—</span>

  return (
    <>
      <div className="flex items-center gap-1.5">
        {salon.plan ? planLabel(salon.plan) : <span className="text-ink-faint">нет</span>}
        {salon.planGift && <Badge tone="info">подарок</Badge>}
      </div>
      {salon.planRenewsAt && (
        <Sub>
          {salon.planGift ? 'бесплатно до' : 'до'} {formatDate(salon.planRenewsAt)}
        </Sub>
      )}
      {salon.pendingPlan && <Sub>ждёт оплаты {planLabel(salon.pendingPlan)}</Sub>}
      {salon.payments > 0 && (
        <Sub>
          оплат <span className="tabular">{salon.payments}</span>
        </Sub>
      )}
      {onGrant && (
        <button
          type="button"
          onClick={onGrant}
          className="min-h-11 text-[13px] font-medium text-ink-muted hover:text-ink hover:underline md:min-h-8"
        >
          Подарить…
        </button>
      )}
    </>
  )
}
